import {
  AdditionalDocument,
  AddTimeWorkerData,
  AgreementBusinessType,
  AIChatInput,
  BankAccount,
  BasicSite,
  EmailTemplate,
  HMRCSetting,
  HourType,
  Image,
  OrderByItem,
  Passport,
  PayrollPayload,
  PayrunPayload,
  PaySchedule,
  PayTagGroup,
  PullTimelogsStrategy,
  SiteShift,
  SMSTemplate,
  Statuses,
  TeamMember,
  Testimonial,
  TimeOrderByItem,
  TrackEventType,
  AIChatInputTool,
  FormQuestion,
  StaffListItem,
  CompanyWorkerItem,
  UpdateIAMUserData,
  OnboardingStatusFilter,
  TableHovers,
  InviteItemReason,
  InviteItemResourceType,
  InviteItemStatus,
} from '../types';
import { GridCellValue, GridRowId } from '@mui/x-data-grid';

export interface UserExists {
  status: boolean;
  user_id: string | null;
  email_exists: boolean | null;
  phone_exists: boolean | null;
  ni_number_exists: boolean;
  email: string | null;
  phone: string | null;
  ni_number: string | null;
  is_deleted: boolean | null;
}

export interface UserExistsResponse {
  status: boolean;
  user_id: string;
  email_exists: boolean;
  phone_exists: boolean;
  ni_number_exists: boolean;
}

export interface StatusMessage {
  status: boolean;
  message: string;
}

export interface PaginatedList<T> {
  count: number;
  items: T[];
}

export interface SortBy {
  field_name: string;
  desc?: boolean;
}

export interface BasicSearch {
  offset?: number;
  limit?: number;
  order_by?: SortBy[];
}

export interface Token {
  access_token: string;
  token_type: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
}

export interface GetUsersData {
  substring?: string;
  id_status?: string;
  id_verified?: boolean;
  rtw_verified?: boolean;
  rtw_status?: string;
  business_types?: string[];
  vat_registered?: boolean;
  offset?: number;
  limit?: number;
  order_by?: OrderByItem[];
}

export interface GetUserArgs {
  token: string;
  id: number;
}

export interface UpdateUserArgs {
  userId: string;
  data: UpdateIAMUserData;
}

export interface ChangeUserPasswordArgs {
  id: string;
  data: ChangePasswordData;
}

export interface LoginData {
  username: string;
  password: string;
  client_secret: string;
}

export interface GetTagsArgs {
  group?: string;
  limit?: number;
  offset?: number;
  short?: boolean;
}

export interface UpdateTagArgs {
  name: GridCellValue;
  id: GridRowId;
  group: string;
  verified: boolean;
}

export interface CreateTagArgs {
  name: GridCellValue;
  group: string;
}

export interface DeleteTagArgs {
  id: GridRowId;
}

export interface CreateUserData {
  password_1: string;
  password_2: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
}

export interface ChangePasswordData {
  password_1: string;
  password_2: string;
}

export interface CreatSurveyData {
  name: string;
  questions: { text: string }[];
}

export interface GetTimesheets {
  token: string;
  payload: PayrollPayload;
}

export interface CISTimesheetRequest {
  token: string;
  companyId: string;
  payrunId: string;
  payScheduleId: string;
  file: File;
  mapping?: any;
}

export interface ListEmployeeRequest {
  companyId?: string;
  data: ListEmployeeRequestData;
}

export interface ListEmployeeRequestData {
  substring?: string;
  pay_schedule_id?: string;
  id_status?: string;
  rtw_status?: string;
  id_verified?: boolean;
  rtw_verified?: boolean;
  vat_registered?: boolean;
  pay_schemes?: string[];
  taxation_statuses?: string[];
  business_types?: string[];
  job_roles?: string[];
  nationalities?: string[];
  offset?: number;
  limit?: number;
  order_by?: OrderByItem[];
  company_id?: string;
}

export interface ListPaySchedulesRequest {
  companyId?: string;
  substring?: string;
  offset?: number;
  limit?: number;
}

export interface GetPayScheduleTagsRequest {
  companyId?: string;
  payScheduleIds?: string[];
  offset?: number;
  limit?: number;
  substring?: string;
  group: PayTagGroup;
  verified?: boolean;
  order_by?: OrderByItem[];
}

export interface CreatePayScheduleTagRequest {
  companyId: string;
  payScheduleId: string;
  group: PayTagGroup;
  name: string;
  verified: boolean;
  byStaff?: boolean;
}

export interface PatchPayScheduleTagsRequest {
  companyId: string;
  payScheduleId: string;
  payScheduleTagId: string;
  group: PayTagGroup;
  name: string;
  verified: boolean;
}

export interface DeletePayScheduleTagsRequest {
  companyId: string;
  payScheduleId: string;
  payScheduleTagId: string;
}

export interface ListStaffRequest {
  substring?: string;
  offset?: number;
  limit?: number;
  order_by?: OrderByItem[];
  roles?: string[];
}

export interface ListCompanyWorkersRequest {
  offset?: number;
  limit?: number;
  order_by?: OrderByItem[];
  substring?: string;
  modules?: string[];
  resource_ids?: string[];
  // Server default is true; send false to skip the onboarding read (pickers/autocompletes)
  include_onboarding?: boolean;
  onboarding_status?: OnboardingStatusFilter;
}

export interface GetStaffResponse {
  count: number;
  items: StaffListItem[];
}

export interface GetCompanyWorkersResponse {
  count: number;
  items: CompanyWorkerItem[];
}

// Invite-scoped inventories: every resource of the company, regardless of the caller's rights
export interface InviteTeamsSearchRequest {
  offset?: number;
  limit?: number;
  substring?: string;
  team_ids?: string[];
  order_by?: OrderByItem[];
}

export interface InviteLocationsSearchRequest {
  offset?: number;
  limit?: number;
  substring?: string;
  show_deleted?: boolean;
  site_ids?: string[];
  // This route spells the sort key `field`, not `field_name`
  order_by?: { field: string; desc: boolean }[];
}

export interface InvitePaySchedulesSearchRequest {
  offset?: number;
  limit?: number;
  order_by?: OrderByItem[];
}

export interface SendStaffInviteRequest {
  email: string;
  staff_payload: {
    position_name: string;
    roles: SendStaffInviteRole[];
  };
  /**
   * The "Customise" form: one entry per module row the preview returned, each the
   * full ticked list. Send it only when Customise was opened — omitted means role
   * defaults, while [] is an explicit reset that also clears any customisation the
   * person already has in this company once they accept.
   */
  permission_overrides?: InviteModulePermissionOverride[];
}

export interface InviteModulePermissionOverride {
  module_system_name: string;
  /** The complete ticked list for the module — never a diff. */
  permissions: string[];
}

export interface SendStaffInviteRole {
  role_id: string;
  resource_type: string;
  resource_ids: string[];
}

export interface UpdateStaffPermissionsRole {
  role_id: string;
  resource_type: string;
  resource_ids: string[];
}

export interface UpdateStaffPermissionsPayload {
  position_name: string;
  roles: UpdateStaffPermissionsRole[];
}

export interface TriggerChecker {
  task_name: string;
  parameters?: any;
  userId?: string;
}

export interface CreateAdditionalDocument {
  document: File;
  userId: string;
  data: Partial<AdditionalDocument>;
}

export interface CreatePasswordManually {
  userId?: string;
  data: Passport;
}

export interface ResetPassword {
  password_1: string;
  password_2: string;
  token: string;
  recaptcha_token: string;
}

export interface GetPayslipsData {
  tax_year_end?: number;
  tax_week_end?: number;
  offset?: number;
  limit?: number;
  order_by?: OrderByItem[];
  company_id?: string;
}

export interface GenerateNextPayrun {
  companyId?: string;
  payScheduleId: string;
  copyPrevious: boolean;
}

export interface GetPayrun {
  token: string;
  companyId: string;
  payrunId: string;
  data: PayrunPayload;
}

export interface GetCompanies {
  offset?: number;
  limit?: number;
  substring?: string;
  type?: string;
  status?: string;
  pay_company?: boolean;
  with_owner?: boolean;
  order_by?: OrderByItem[];
}

export interface GetEmployeeTimesheetsData {
  offset?: number;
  limit?: number;
  company_id?: string;
  tax_week_start?: number;
  tax_week_end?: number;
  tax_year_start?: number;
  tax_year_end?: number;
  pay_period?: {
    start: string;
    end: string;
  };
  pay_scheme?: string;
  pay_run_status?: string;
  pay_tax_status?: string;
  paid?: boolean;
  order_by?: OrderByItem[];
}

export interface GetEmployeeTimesheetsRequest {
  userId: string;
  companyId?: string;
  data: GetEmployeeTimesheetsData;
}

export interface GetPayslipsRequest {
  userId?: string;
  companyId?: string;
  data: GetPayslipsData;
}

export interface CreateTeamData {
  logo?: Image | null;
  name: string;
  description?: string;
  manager_id?: string;
  location_ids?: string[];
  teamManagers?: { uuid: string; name: string }[];
}

export interface TeamData {
  description: string;
  logo: string | null;
  member_count: number;
  name: string;
  uuid: string;
  location_ids?: string[];
}

export interface ChangeTeamManagersData {
  to_add?: string[];
  to_remove?: string[];
}

export interface GetTimesheetData {
  offset: number;
  limit?: number;
  substring?: string;
  pay_scheme?: string;
  order_by?: OrderByItem[];
}

export interface RegisterPayCompanyData {
  pay_schedule: PaySchedule;
  hmrc_settings: Partial<HMRCSetting> | null;
  pay_run: any;
  bank_account: BankAccount;
}

export interface RemoveEmployeeRequest {
  userId: string;
  companyId?: string;
  payScheduleId?: string;
}

export interface RemoveEmployeeFromModuleRequest {
  token: string;
  userId: string;
  companyId?: string;
}

export interface MarkAsPaidRequest {
  token: string;
  companyId: string;
  payScheduleId: string;
  payrunId: string;
  employeeId?: string;
}

export interface SendPayslipsRequest {
  token: string;
  companyId?: string;
  payrunLineId: string;
}

export interface SendDocumentRequest {
  token: string;
  userId: string;
  companyId?: string;
}

export interface SendBulkPayStatements {
  token: string;
  companyId?: string;
  payrunId: string;
}

export interface CreateTemplateRequest {
  token: string;
  data: EmailTemplate | SMSTemplate;
}

export interface UpdateTemplateRequest {
  token: string;
  templateId: string;
  data: EmailTemplate | SMSTemplate;
}

export interface DeleteTemplateRequest {
  token: string;
  templateId: string;
}

export interface SendSMSRequest {
  templateId: string;
  users: string[];
  phones?: string[];
  scheduled?: string;
}

export interface SendCustomSMSRequest {
  token: string;
  users: string[];
  phones?: string[];
  scheduled?: string;
  message: string;
}

export interface SendEmailRequest {
  templateId: string;
  users: string[];
  emails?: string[];
  scheduled?: string;
}

export interface SendEmailToMeRequest {
  token: string;
  templateId: string;
  userId: string;
  email: string;
}

export interface SendSMSToMeRequest {
  token: string;
  templateId: string;
  userId: string;
  phone: string;
}

export interface GetAgreementsRequest {
  token: string;
  userId?: string;
  substring?: string;
  pay_scheme?: string;
  offset?: number;
  limit?: number;
  companyId?: string;
  order_by?: OrderByItem[];
}

export interface GetAgreementRequest {
  token: string;
  payScheduleId: string;
  userId?: string;
  companyId?: string;
}

export interface RefreshPayrolleeDocRequest {
  token: string;
  // Required by the admin/legend and employer refresh endpoints (in the path); the self endpoints ignore it.
  userId?: string;
  // Required by the employer refresh endpoints (in the path); used for the pay:can_read_pay_run check.
  companyId?: string;
  payScheduleId: string;
}

export interface GetAgreementVersionsRequest {
  token: string;
  userId: string;
  payScheduleId: string;
}

export interface ReissueAgreementRequest {
  token: string;
  userId: string;
  payScheduleId: string;
  businessType: AgreementBusinessType;
  // The date printed inside the reissued contract, YYYY-MM-DD.
  agreementDate: string;
  note?: string;
}

export interface UpdateAgreementVersionNoteRequest {
  token: string;
  userId: string;
  payScheduleId: string;
  version: number;
  // Empty string and null both remove the note.
  note?: string | null;
}

export interface MakeAgreementVersionCurrentRequest {
  token: string;
  userId: string;
  payScheduleId: string;
  version: number;
  note?: string;
}

export interface HideAgreementVersionRequest {
  token: string;
  userId: string;
  payScheduleId: string;
  version: number;
  reason: string;
}

export interface DownloadHealthAndSafetyPDFRequest {
  userId?: string;
}

export interface UploadPayScheduleContractWrapperRequest {
  payScheduleId: string;
  companyId: string;
  contract: File;
}

export interface GetCompanyBankAccountRequest {
  companyId: string;
}

export interface CreateOrUpdateCompanyBankAccountRequest {
  companyId: string;
  bankAccount: BankAccount;
}

export interface GetCompanyTeamsRequest {
  companyId: string;
  requestData: GetCompanyTeamsDataRequest;
}

export interface TeamCertificateFilter {
  type: string;
  status: boolean;
}

export interface TeamsDataRequest {
  offset?: number;
  limit?: number;
  substring?: string;
  survey_tag?: string;
  team_ids?: string[];
  role?: string;
  rtw_status?: string;
  id_status?: string;
  certificate?: TeamCertificateFilter[];
  nationality?: string;
  gender?: string;
  age?: {
    gte: number;
    lte: number;
  };
}

/** How the selected qualifications are matched against a worker. */
export type QualificationMatchMode = 'has_any' | 'has_all' | 'has_none';

/** Request body for dashboards/widgets/teams/.../custom/qualifications */
export interface TeamQualificationsWidgetRequest {
  offset: number;
  limit: number;
  match_mode?: QualificationMatchMode;
  statuses?: string[];
  qualifications?: string[];
  team_ids?: string[];
  user_ids?: string[] | null;
  job_titles?: string[];
}

export type CompanyTeamsSortingOption = 'created_at' | 'updated_at' | 'name';

export interface GetCompanyTeamsDataRequest extends TeamsDataRequest {
  order_by: {
    field_name: CompanyTeamsSortingOption;
    desc: boolean;
  }[];
}

export interface GetCompanyTeamsMembersDataRequest extends TeamsDataRequest {
  order_by: {
    field_name: keyof TeamMember;
    desc: boolean;
  }[];
}

export interface MemberDTO {
  user_id: string;
  is_manager?: false;
}

export interface DeleteMemberRequestDTO {
  members: string[];
}

export interface AddMemberRequestDTO {
  members: MemberDTO[];
}

// POST /public/v1/invites/companies/{company_id}/workers — one request invites new
// contacts and existing users into any mix of module targets. Processing is async
// (202); the caller gets a Vyce message with the summary when send_report is true.
export type InviteResourceType = 'team' | 'location' | 'pay_schedule';

export type InvitePayScheme = 'self-employed' | 'cis' | 'paye' | 'umbrella';

export type InviteTargetItem =
  | { resource_type: 'team'; resource_id: string }
  // A worker is always added to a specific shift, so shift_id is required
  | { resource_type: 'location'; resource_id: string; shift_id: string }
  | { resource_type: 'pay_schedule'; resource_id: string; pay_scheme: InvitePayScheme };

export interface InviteContact {
  email?: string;
  phone?: string;
}

// "Select all" semantics: everyone currently in these resources. Ids the caller cannot
// see are skipped server-side. all_users: true overrides the three lists.
export interface InviteSource {
  team_ids: string[];
  location_ids: string[];
  pay_schedule_ids: string[];
  all_users: boolean;
}

export interface CreateWorkerInvitesPayload {
  items: InviteTargetItem[];
  contacts: InviteContact[];
  user_ids: string[];
  source: InviteSource;
  // Removed from the final set wherever they came from (lists, sources or contacts)
  exclude_user_ids: string[];
  send_report: boolean;
}

export interface CreateWorkerInvitesRequest {
  companyId: string;
  payload: CreateWorkerInvitesPayload;
}

export interface CreateWorkerInvitesResponse {
  status: boolean;
  workflow_id: string;
  // Display-ready; its wording depends on send_report
  message: string;
}

// Server-side validation limits (422 above them)
export const INVITE_CONTACTS_LIMIT = 500;
export const INVITE_USER_IDS_LIMIT = 1000;

// Filters over an invite's items (places). They match an invite with at least one item that
// satisfies ALL of them together — "to Leeds, by Ann, failed" is one place. Staff invites never match.
export interface InviteItemFilters {
  resource_type?: InviteItemResourceType;
  resource_id?: string;
  invited_by?: string;
  // ISO datetime, inclusive; without a timezone it is read as UTC
  invited_from?: string;
  invited_to?: string;
  item_statuses?: InviteItemStatus[];
  reasons?: InviteItemReason[];
}

export interface GetInvitesRequest extends InviteItemFilters {
  companyId?: string;
  substring?: string;
  offset?: number;
  limit?: number;
  order_by?: OrderByItem[];
  statuses?: string[];
  modules?: string[];
  // Server default is true; send false to skip the onboarding read
  include_onboarding?: boolean;
  onboarding_status?: OnboardingStatusFilter;
}

// 202 — the retry runs after the response; re-run the search to see the line settle
export interface RetryInviteItemResponse {
  status: boolean;
  workflow_id: string;
  // Display-ready, e.g. "Retrying Leeds — Kirkstall Road. The result will show on this invite in a moment."
  message: string;
}

export interface RedeemActiveInviteModel {
  model_name: string;
  role: string;
}

export interface RedeemActiveInviteCompany {
  company_name: string;
  models: RedeemActiveInviteModel[];
}

export interface GetUserManagementItemsPayload {
  offset: number;
  limit: number;
  order_by: OrderByItem[];
  substring: string;
  business_types?: string[];
}

export interface GetInvoicesPayload {
  offset?: number;
  limit?: number;
  order_by?: OrderByItem[];
  status?: string;
}

export interface GetUserData {
  userId?: string;
  companyId?: string;
}

export interface GetSyncSettingsData {
  teamId: string;
  companyId: string;
}

export interface AddEmployeeToPayScheduleRequest {
  companyId: string;
  employeeId: string;
  payScheduleId: string;
  payScheme: string;
  notifyEmployee: boolean;
  notifyEmployer: boolean;
}

export interface RoutineDTO {
  task_for: string;
  initiated_by: string;
  task_name: string;
  uuid: string;
  status: Statuses;
  date_done: string;
  progress: number;
  message: string;
}

export interface TeamPublicLinkRequest {
  companyId: string;
  teamId: string;
}

export interface DownloadCISReportRequest {
  companyId?: string;
  reportId: string;
}

export interface RefreshCISReportRequest {
  companyId: string;
  taxYear: number;
  taxMonth: number;
}
export interface SendCISReportRequest {
  companyId: string;
  taxYear: number;
  taxMonth: number;
}

export interface UploadRTWDocumentRequest {
  file: File;
  userId?: string;
}

export interface GetSitesPayload {
  offset?: number;
  limit?: number;
  order_by?: OrderByItem[];
  substring?: string;
  show_deleted?: boolean;
  only_active?: boolean;
  only_inactive?: boolean;
}

export interface UpdateShiftRequest {
  companyId: string;
  siteId: string;
  shiftId: string;
  shift: SiteShift;
}

export interface CreateShiftRequest {
  companyId: string;
  siteId: string;
  shift: SiteShift;
}

export interface UpdateSiteRequest {
  companyId: string;
  siteId: string;
  site: BasicSite;
}

export interface AddSiteWorkerRequest {
  companyId: string;
  siteId: string;
  shiftId: string;
  worker: AddTimeWorkerData;
}

export interface GetSiteWorkersRequest {
  companyId: string;
  payload: GetSiteWorkersRequestPayload;
}

export interface GetEventsRequest {
  companyId: string;
  payload: GetEventsRequestPayload;
}

export interface GetEventsRequestPayload {
  offset?: number;
  limit?: number;
  order_by: any[];
  user_id: string;
  site_id?: string;
  shift_id?: string;
  event_type?: TrackEventType;
  hour_type?: HourType;
  within_area?: boolean;
  no_zero_lines?: boolean;
  time_period?: {
    gte: string;
    lte: string;
  };
}

export interface GetTimePeriodsRequest {
  companyId: string;
  payload: GetTimePeriodsRequestPayload;
}

export interface GetTimePeriodsRequestPayload {
  user_id: string;
  shift_id: string;
  start_date: string;
  end_date: string;
  no_unpaid_breaks?: boolean;
}

export interface GetSiteWorkersRequestPayload {
  offset?: number;
  limit?: number;
  order_by?: any[];
  substring?: string;
  site_ids?: string[];
  shift_id?: string;
  only_checked_in?: boolean;
  only_not_checked_in?: boolean;
  job_title?: string;
}
export interface GetSiteWorkersSearchRequestPayload {
  offset?: number;
  limit?: number;
  order_by?: any[];
  substring?: string;
  site_id?: string;
  shift_id?: string;
}

export interface TeamsSyncSettingsDTO {
  recent_pay_periods: number;
  recent_time_periods: number;
  strategy?: string;
  active: boolean;
  pay_schedules: string[];
  time_sites: string[];
}

export interface CreateCompanyPhotoData {
  file?: File | null;
  title: string;
  description?: string;
}

export interface CreateTestimonial {
  token: string;
  companyId: string;
  testimonial: Testimonial;
  file?: File | null;
}

export interface ManualClockInOut {
  site_id: string;
  shift_id: string;
  event_type: string;
  within_area: boolean;
  geo_point?: string;
  timestamp: string;
  platform: string;
}

export interface BulkClockOutPayload {
  /** Locations the worker is clocked in on now. Omitted = every location the caller manages. */
  site_ids?: string[];
  /** Omitted = everyone clocked in on those locations. At most BULK_CLOCK_OUT_USER_IDS_LIMIT. */
  user_ids?: string[];
  /** UK wall-clock, no offset: the gateway drops any offset rather than converting it. */
  timestamp: string;
}

export type BulkClockOutSkipReason =
  | 'not_checked_in'
  | 'checked_in_after_timestamp'
  | 'not_in_selected_locations';

export interface BulkClockOutDTO {
  clocked_out: { user_id: string; site_id: string; event_id: string }[];
  skipped: { user_id: string; reason: BulkClockOutSkipReason }[];
}

export interface ActiveCheckInDTO {
  user_id: string;
  site_id: string;
  shift_id: string;
  event_type: 'check_in' | 'check_out';
  hour_type: 'out_of_bounds';
  within_area: boolean;
  geo_point: string;
  interval: 0;
  timestamp: string;
  device: string;
  platform: string;
  model: string;
  manually_added: boolean;
  uuid: string;
  created_at: string;
  updated_at: string;
  deleted_at: null;
  lon: number;
  lat: number;
}

export interface WorkerLookupDTO {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  gender: string;
  date_of_birth: string;
  avatar?: string;
}

export interface CheckInWorkersRequestProps {
  offset?: number;
  limit?: number;
  order_by?: TimeOrderByItem[];
  worker_substring?: string;
  site_substring?: string;
  site_ids?: string[];
}

export interface CheckOutWorkersRequestProps extends CheckInWorkersRequestProps {
  time_period?: {
    gte: string;
    lte: string;
  };
  worker_substring?: string;
}

export interface TimeLogsRequestProps {
  offset?: number;
  limit?: number;
  order_by?: TimeOrderByItem[];
  substring?: string;
  start_date?: string;
  end_date?: string;
}

export interface TimeLogsByWorkerRequest {
  offset?: number;
  limit?: number;
  order_by?: TimeOrderByItem[];
  substring?: string;
  site_id?: string;
  start_date?: string;
  end_date?: string;
  check_in_ok?: boolean;
  check_out_ok?: boolean;
  face_in_ok?: boolean;
  face_out_ok?: boolean;
  auto_clock_out?: boolean;
  manual_clock_out?: boolean;
}

export interface CheckInWorkerDTO {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  gender: string;
  site_id: string;
  shift_id: string;
  site_name: string;
  checked_in: string;
  role: string;
  photo_url: string;
  ni_number: string;
  job_title: string;
}

export interface DormantWorkersRequestProps {
  offset?: number;
  limit?: number;
  order_by?: TimeOrderByItem[];
  worker_substring?: string;
  site_ids?: string[];
  from_date: string;
}

export interface DormantWorkerDTO {
  user_id: string;
  first_name: string;
  last_name: string;
  photo_url: string;
  last_clock_in_date: string;
  last_clock_in_site_uuid: string;
  last_clock_in_site_name: string;
}

export interface EmployeesVATRequestProps {
  substring?: string;
  company_id?: string;
  verified?: boolean;
  offset?: number;
  limit?: number;
  order_by?: OrderByItem[];
}

export interface EmployeeVATDTO {
  user_id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
  avatar: string | null;
  ni_number: string;
  vat_number: string;
  /** Null for workers verified before we started recording the date — expected, not an error. */
  date_verified: string | null;
  /** Null for workers who have never been paid. */
  last_paid_date: string | null;
  verified_status: boolean;
}

export interface AutoUserDeletionSettings {
  enabled: boolean;
  inactive_days: number;
}

export type ExpiringDocumentType = 'rtw' | 'id' | 'qualification';

export type ExpiringDocumentsFrequency = 'daily' | 'weekly' | 'specific_day';

export interface ExpiringDocumentsSettings {
  uuid: string; // read-only, present in GET responses
  name: string; // required, 1–127 chars
  enabled: boolean;
  frequency: ExpiringDocumentsFrequency;
  day_of_week: number | null; // 0..6, Mon=0 … Sun=6
  day_of_month: number | null; // 1..31
  hour: number; // 0..23 UTC
  recipient_user_ids: string[];
  team_ids: string[]; // [] = all teams
  document_types: ExpiringDocumentType[]; // [] = all three
}

// POST / PUT body — the full object without the server-owned uuid.
export type ExpiringDocumentsSettingPayload = Omit<ExpiringDocumentsSettings, 'uuid'>;

export interface ExpiringDocumentsSettingsListResponse {
  items: ExpiringDocumentsSettings[];
}

export interface CompanySettingsDTO {
  company_id: string;
  time_settings: { auto_user_deletion?: AutoUserDeletionSettings };
  payroll_settings?: Record<string, unknown>;
  notifications_settings?: Record<string, unknown>;
  teams_settings?: Record<string, unknown>;
  forms_settings?: Record<string, unknown>;
  messaging_settings?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  updated_by: string;
}

export interface CheckOutWorkerDTO {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  gender: string;
  ni_number: string;
  site_id: string;
  shift_id: string;
  site_name: string;
  checked_out: string;
  role: string;
  photo_url: string;
  clocked_hours: number;
  paid_hours: number;
}

interface PayScheduleLink {
  company_id: string;
  pay_schedule_id: string;
  pay_scheme: string;
}

interface TimesheetEmployee {
  uuid: string;
  first_name: string;
  last_name: string;
  ni_number: string;
  worker_reference: string;
}

export interface TimesheetLineDTO {
  uuid: string;
  basic: number;
  overtime: number;
  mileage: number;
  gross: number;
  basic_units: number;
  basic_rate: number;
  overtime_units: number;
  overtime_rate: number;
  adjustments: number;
  expenses: number;
  miles: number;
  mile_rate: number;
  employee: TimesheetEmployee;
  pay_schedule_link: PayScheduleLink;
  total_pay: number;
}

export interface TimesheetByStaffDTO {
  pay_run_id: string;
  submitted_by: {
    uuid: string;
    first_name: string;
    last_name: string;
  };
  status: string;
  totals: {
    gross: number;
    workers: number;
  };
  start_date: string;
  end_date: string;
  tax_year_start: number;
  tax_week_start: number;
  tax_year_end: number;
  tax_week_end: number;
  payment_date: string;
  pay_schedule: {
    uuid: string;
    name: string;
    pay_frequency: string;
    pay_weekday: number;
    payroll_paid_by_worker: boolean;
    pay_last_workday: boolean;
    outsourced: boolean;
    custom_contract: {
      uuid: string;
      created_at: string;
      updated_at: string;
      pay_schedule_id: string;
      body_s3_key: string;
      body_url: string;
    };
    sms_pay_statement_cron: string;
    email_pay_statement_cron: string;
    sms_pay_statement: string;
    email_pay_statement: string;
  };
  company: {
    company_id: string;
    name: string;
    trading_name: string;
  };
  timesheet_lines: { count: number; items: TimesheetLineDTO[] };
  hovers?: TableHovers;
}

export interface EditWorkerHoursRequestProps {
  companyId: string;
  siteId: string;
  userId: string;
  shiftId: string;
  day: string;
  amendments: {
    basic_amendment: number;
    overtime_amendment: number;
  };
}

export interface PullTimelogsRequestProps {
  payrunId: string;
  companyId: string;
  strategy: PullTimelogsStrategy;
  approved: boolean;
}

export interface ExportTimeLogsRequestData {
  companyId: string;
  data: {
    sites_ids?: string[];
    // omit or null to export all workers
    users_ids?: string[] | null;
    period: {
      start: string;
      end: string;
    };
    // null exports both approved and not approved
    approved?: boolean | null;
  };
}

export interface ExportTimeLogsDTO {
  file_name: string;
  // presigned link, valid until expires_at (~15 min) — never store it
  url: string;
  expires_at: string;
  size_bytes: number;
}

export interface GetTimeLogsPayload {
  offset?: number;
  limit?: number;
  order_by: any[];
  start_date?: string;
  end_date?: string;
  company_id?: string;
  site_id?: string;
}

export interface TimeLogDTO {
  user_id: string;
  work_day: string;
  company_id: string;
  site_id: string;
  shift_id: string;
  site_name: string;
  shift_name: string;
  basic_hours: number;
  basic_minutes: number;
  basic_amendment_hours: number;
  basic_amendment_minutes: number;
  overtime_hours: number;
  overtime_minutes: number;
  overtime_amendment_hours: number;
  overtime_amendment_minutes: number;
  clocked_in_hours: string;
  clocked_out_hours: string;
  approved: true;
}

export interface TimeLogTotal {
  basic_hours: number;
  basic_minutes: number;
  basic_amendment_hours: number;
  basic_amendment_minutes: number;
  overtime_hours: number;
  overtime_minutes: number;
  overtime_amendment_hours: number;
  overtime_amendment_minutes: number;
}

export interface AddTimePeriodPayload {
  user_id: string;
  workday: string;
  shift_id: string;
  site_id: string;
  check_in: string;
  check_out: string;
  check_in_ok: boolean;
  check_out_ok: boolean;
  face_in_ok: boolean;
  face_out_ok: boolean;
  manual_check_in: boolean;
  manual_check_out: boolean;
}

export interface CustomLocationFieldsDetailsDTO {
  user_id: string;
  company_id: string;
  created_at: string;
  updated_at: string;
  systemic_fields: {
    count: number;
    items: LocationFieldDetailsDTO[];
  };
  custom_fields: {
    count: number;
    items: LocationFieldDetailsDTO[];
  };
}
export interface LocationFieldDetailsDTO {
  name: string;
  system_name: string;
  value?: string;
  position: number;
  is_active: boolean;
}

export interface ReportSettingsDTO {
  time_logs_fields: {
    data: {
      fields: {
        count: number;
        items: LocationFieldDetailsDTO[];
      };
    };
    detailed_summary_tab_data: {
      fields: {
        count: number;
        items: LocationFieldDetailsDTO[];
      };
    };
  };
  location_fields: {
    systemic_fields: {
      count: number;
      items: LocationFieldDetailsDTO[];
    };
    custom_fields: {
      count: number;
      items: LocationFieldDetailsDTO[];
    };
  };
}

export interface UpdatedReportSettingsData {
  time_logs_fields?: {
    fields: LocationFieldDetailsDTO[];
  };
  location_fields?: {
    fields: LocationFieldDetailsDTO[];
  };
}

export interface CustomLocationFieldsDTO {
  site_id: string;
  user_id: string;
  company_id: string;
  values: CustomLocationField[];
  created_at: string;
  updated_at: string;
}

export interface CustomLocationField {
  system_name: string;
  name: string;
  value: string;
}
export interface TeamQualificationWidgetDTO {
  user_id: string;
  first_name: string;
  last_name: string;
  gender: string;
  photo: string;
  title: string;
  expiry_date: string;
  status: string;
  certificate_id: string;
  email: string;
  phone: string;
}

export interface CompanyRoleDTO {
  role_id: string;
  role_name: string;
  resource_type: string;
  /**
   * What the role grants, as permission **display names** in a deliberate order
   * ('Edit locations'), so it renders as-is. The list spans modules — a Time role
   * also names 'Invite workers', which lives under Company — and the owner role
   * is spelled ['Full access']. To turn it into catalogue system_names use
   * roleDefaultPermissions (rolesModule/utils/permissionState).
   */
  permission_names?: string[];
}

// Keyed by module display name ('Company', 'Payroll', 'Teams', 'Time & Attendance', …).
// An index signature rather than fixed keys — the module list comes from the backend now.
export interface CompanyRolesResponseDTO {
  [moduleDisplayName: string]: CompanyRoleDTO[];
}

export interface PermissionDTO {
  permission_id: string;
  system_name: string;
  display_name: string;
  description: string;
  /** Permissions to tick and lock automatically when this one is ticked. */
  implied_permissions: string[];
}

export interface PermissionGroupDTO {
  group: string;
  permissions: PermissionDTO[];
}

export interface PermissionCatalogueModuleDTO {
  module_system_name: string;
  module_display_name: string;
  groups: PermissionGroupDTO[];
}

export interface ModulePermissionOverridesDTO {
  module_system_name: string;
  module_display_name: string;
  /** What the role alone gives. */
  role_defaults: string[];
  added: string[];
  removed: string[];
  /** The boxes to show as ticked. */
  effective: string[];
}

export interface UpdateModulePermissionOverridePayload {
  module_system_name: string;
  /** The complete ticked list for the module — never a diff. */
  permissions: string[];
  /** The "Apply to all" flag: also deletes this module's per-resource exceptions. */
  clear_resource_overrides?: boolean;
}

export interface PermissionOverridesPreviewPayload {
  /** At least one. Only role_id is read — scope decides where a permission applies, not which. */
  roles: { role_id: string }[];
  /** Leave out for the role defaults; send the ticks to see what the invite would store. */
  permissions?: InviteModulePermissionOverride[];
}

export interface ResourcePermissionOverrideItemDTO {
  module_system_name: string;
  resource_id: string;
  resource_type: string;
  added: string[];
  removed: string[];
  effective: string[];
  has_exception: boolean;
}

export interface ResourcePermissionOverridesResponseDTO {
  total: number;
  module_defaults: Record<string, string[]>;
  items: ResourcePermissionOverrideItemDTO[];
}

export interface GetResourcePermissionOverridesParams {
  modules?: string[];
  offset?: number;
  limit?: number;
}

export interface UpdateResourcePermissionOverridePayload {
  /** The complete ticked list for this resource. Sending the module default clears the exception. */
  permissions: string[];
}

// 409 body: a removed permission is still required by another one the person keeps.
export interface PermissionRequirementPair {
  permission: string;
  requires: string;
}

export interface ClockedOutRolesDataRequest {
  offset?: number;
  limit?: number;
  site_ids?: string[];
  order_by?: {
    field: string;
    desc: boolean;
  }[];
  worker_substring?: string;
  time_period?: { lte: string; gte: string };
}

export interface Cis300SubmitsRequest {
  offset?: number;
  limit?: number;
  order_by?: OrderByItem[];
  hmrc_settings_id: string;
  tax_month: number;
  tax_year: number;
}

export interface EmployeeLocationsRequest {
  companyId: string;
  userId: string;
  payload: {
    limit: number;
    offset: number;
    order_by: {
      field: string;
      desc: boolean;
    }[];
    substring?: string;
    only_active?: boolean;
    only_inactive?: boolean;
    show_deleted?: boolean;
  };
}

export interface TeamMembersWithoutRateDTO {
  user_id: string;
  gender: string;
  photo_url: string;
  company_id: string;
  first_name: string;
  last_name: string;
  locations: string[];
}

export interface CreateResponseAiRequest {
  input: AIChatInput[] | string;
  model?: string;
  assistantId?: string;
  tools?: AIChatInputTool[];
  signal?: AbortSignal;
}

export interface LocationUsersBarchart {
  not_clocked_in_today: number;
  clocked_in: number;
  clocked_out: number;
}

type BaseSettingOption = {
  type: 'number' | 'bool';
  default: boolean | string;
};

type SettingOption = { type: 'enum'; default: string; options: string[] } | BaseSettingOption;

export type SettingsOptions = {
  uuid: string;
  name: string;
  active: SettingOption;
} & Record<string, SettingOption>;

export interface ShiftRuleSchema {
  uuid: string;
  name: string;
  description: string;
  settings: SettingsOptions;
}

export interface ModuleCandidatatesSchema {
  offset?: number;
  limit?: number;
  site_ids?: string[];
  substring: string;
  order_by?: {
    field_name?: string;
    field?: string;

    desc: boolean;
  }[];
}

export interface CreateFormRequest {
  companyId: string;
  userId: string;
  title: string;
  description?: string;
  questions?: FormQuestion[];
  startDate?: any | null;
  endDate?: any | null;
  allowMultipleResponses?: boolean;
  status?: string;
  signature?: boolean;
  userName?: string;
  clockInBlock?: boolean;
  templateId?: string | null;
  folderId?: string | null;
}

export interface UpdateFormRequest extends CreateFormRequest {
  formId: string;
}

export interface GetFormsRequest {
  offset: number;
  limit: number;
  substring: string;
  companyId: string;
  userId: string;
  status?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
  filterType?: 'created' | 'edited' | 'participated';
  folderId?: string | null;
  includeDescendants?: boolean;
}

export interface FormFolderDoc {
  id: string;
  name: string;
  companyId: string;
  createdBy: string;
  parentId?: string | null;
  path?: string[];
  depth?: number;
  archived?: boolean;
  colour?: string | null;
  createdAt?: { _seconds: number } | Date;
  updatedAt?: { _seconds: number } | Date;
  formsCount?: number;
}

export interface FormTemplateFolderDoc {
  id: string;
  name: string;
  companyId: string;
  createdBy: string;
  parentId?: string | null;
  path?: string[];
  depth?: number;
  archived?: boolean;
  colour?: string | null;
  createdAt?: { _seconds: number } | Date;
  updatedAt?: { _seconds: number } | Date;
  templatesCount?: number;
}

export interface GetFormsToAnswerRequest {
  companyId: string;
  participant: { email: string; phone: string };
  limit?: number;
  offset?: number;
}

// Form template types
export interface FormTemplateDoc {
  id?: string;
  title: string;
  description?: string;
  questions?: FormQuestion[];
  signature?: boolean;
  clockInBlock?: boolean;
  createdBy: string;
  createdByName?: string | null;
  createdAt?: { _seconds: number } | Date;
  updatedAt?: { _seconds: number } | Date;
  companyId: string;
  duplicatedFromTemplateId?: string | null;
  archived?: boolean;
  folderId?: string | null;
  folderName?: string | null;
  folderPath?: string[];
}

export interface GetFormTemplatesRequest {
  companyId: string;
  userId: string;
  limit?: number;
  offset?: number;
  search?: string | null;
  folderId?: string | null;
  archived?: boolean;
}

export interface CreateFormTemplateRequest {
  companyId: string;
  userId: string;
  userName?: string | null;
  title: string;
  description?: string;
  questions?: FormQuestion[];
  signature?: boolean;
  clockInBlock?: boolean;
  folderId?: string | null;
}

export interface UpdateFormTemplateRequest {
  companyId: string;
  templateId: string;
  userId: string;
  title?: string;
  description?: string;
  questions?: FormQuestion[];
  signature?: boolean;
  clockInBlock?: boolean;
  folderId?: string | null;
}

export interface CreateFormFromTemplateRequest {
  companyId: string;
  templateId: string;
  userId: string;
  userName?: string | null;
  customizations?: {
    title?: string;
    description?: string;
    questions?: FormQuestion[];
    allowMultipleResponses?: boolean;
    startDate?: Date | null;
    endDate?: Date | null;
  };
  saveAs?: 'form' | 'template';
}

export interface CreateUserRequest {
  password_1: string;
  password_2: string;
  phone: string;
  email: string;
  first_name: string;
  last_name: string;
  gender?: string | null;
  date_of_birth?: string;
  ni_number?: string;
}

export interface BulkEditTimeLogsRequestPayload {
  company_id: string;
  user_ids?: string[];
  shift_id: string;
  workday: string;
  custom_periods: {
    time_from: string;
    time_to: string;
    hour_type: string;
    exclude: boolean;
  }[];
}

export interface BulkAmendTimeLogsRequest {
  company_id: string;
  shift_id: string;
  site_id: string;
  workday: string;
  user_id: string;
  payload: {
    user_ids?: string[];
    basic_amendment: number;
    overtime_amendment: number;
    paid_break_amendment: number;
    unpaid_break_amendment: number;
  };
}
